import { v4 as uuidv4 } from 'uuid';
import type { CriteriaCondition } from './types';
import type { OperatorType, FieldDefinition } from '../../schemas/types';
import { OPERATOR_DSL, getSchemaForEntityType } from '../../schemas';

/**
 * Convert conditions to DSL string
 */
export function conditionsToDsl(conditions: CriteriaCondition[]): string {
  return conditions
    .map((c) => {
      const op = OPERATOR_DSL[c.operator];
      let valueStr: string;

      if (Array.isArray(c.value)) {
        if (c.operator === 'between' && c.value.length === 2) {
          valueStr = `${c.value[0]} AND ${c.value[1]}`;
        } else {
          valueStr = `[${c.value.map((v) => (typeof v === 'string' ? `'${v}'` : v)).join(', ')}]`;
        }
      } else if (typeof c.value === 'string') {
        valueStr = `'${c.value}'`;
      } else {
        valueStr = String(c.value);
      }

      return `${c.path} ${op} ${valueStr}`;
    })
    .join('\n');
}

/**
 * Parse DSL string to conditions
 */
export function dslToConditions(dsl: string): CriteriaCondition[] {
  const lines = dsl.split('\n').filter((line) => line.trim());
  const conditions: CriteriaCondition[] = [];

  for (const line of lines) {
    try {
      const condition = parseDslLine(line);
      if (condition) {
        conditions.push(condition);
      }
    } catch {
      // Skip invalid lines
    }
  }

  return conditions;
}

/**
 * Parse a single DSL line
 */
function parseDslLine(line: string): CriteriaCondition | null {
  const trimmed = line.trim();
  if (!trimmed) return null;

  // Find operator
  const operatorMap: Record<string, OperatorType> = {
    '>=': 'gte',
    '<=': 'lte',
    '>': 'gt',
    '<': 'lt',
    '=': 'eq',
    CONTAINS: 'contains',
    contains: 'contains',
    BETWEEN: 'between',
    between: 'between',
  };

  // Sort by length to match longest first
  const operators = Object.keys(operatorMap).sort((a, b) => b.length - a.length);

  let foundOp: string | null = null;
  let opIndex = -1;

  for (const op of operators) {
    const regex = new RegExp(`\\s+${op.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s+`, 'i');
    const match = trimmed.match(regex);
    if (match && match.index !== undefined) {
      foundOp = op;
      opIndex = match.index;
      break;
    }
  }

  if (!foundOp || opIndex === -1) {
    return null;
  }

  const path = trimmed.substring(0, opIndex).trim();
  const valueStr = trimmed
    .substring(opIndex + foundOp.length + 2) // +2 for spaces
    .trim();

  const operator = operatorMap[foundOp.toUpperCase()] || operatorMap[foundOp];
  const value = parseValue(valueStr, operator);

  return {
    id: uuidv4(),
    path,
    operator,
    value,
  };
}

/**
 * Parse value string based on operator
 */
function parseValue(
  valueStr: string,
  operator: OperatorType
): string | number | string[] | [number, number] {
  if (operator === 'between') {
    // Handle "X AND Y" format
    const andMatch = valueStr.match(/^(.+?)\s+AND\s+(.+)$/i);
    if (andMatch) {
      const v1 = parseScalar(andMatch[1].trim());
      const v2 = parseScalar(andMatch[2].trim());
      return [Number(v1), Number(v2)];
    }
  }

  if (operator === 'contains') {
    // Handle array format: ['a', 'b']
    if (valueStr.startsWith('[') && valueStr.endsWith(']')) {
      const content = valueStr.slice(1, -1);
      return content.split(',').map((item) => String(parseScalar(item.trim())));
    }
  }

  return parseScalar(valueStr);
}

/**
 * Parse a scalar value
 */
function parseScalar(valueStr: string): string | number {
  // Quoted string
  if (
    (valueStr.startsWith("'") && valueStr.endsWith("'")) ||
    (valueStr.startsWith('"') && valueStr.endsWith('"'))
  ) {
    return valueStr.slice(1, -1);
  }

  // Number
  if (!isNaN(Number(valueStr))) {
    return Number(valueStr);
  }

  return valueStr;
}

/**
 * Convert conditions to JSON string
 */
export function conditionsToJson(conditions: CriteriaCondition[]): string {
  return JSON.stringify(
    conditions.map((c) => ({
      path: c.path,
      operator: c.operator,
      value: c.value,
    })),
    null,
    2
  );
}

/**
 * Parse JSON string to conditions
 */
export function jsonToConditions(json: string): CriteriaCondition[] {
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) {
      throw new Error('JSON must be an array');
    }

    return parsed.map((item) => ({
      id: uuidv4(),
      path: item.path,
      operator: item.operator,
      value: item.value,
    }));
  } catch {
    return [];
  }
}

/**
 * Create an empty condition
 */
export function createEmptyCondition(entityType: string): CriteriaCondition {
  const schema = getSchemaForEntityType(entityType);
  const firstField = schema?.categories[0]?.fields[0];

  return {
    id: uuidv4(),
    path: firstField?.path || '',
    operator: firstField?.operators[0] || 'eq',
    value: '',
  };
}

/**
 * Get field definition by path
 */
export function getFieldByPath(
  entityType: string,
  path: string
): FieldDefinition | undefined {
  const schema = getSchemaForEntityType(entityType);
  if (!schema) return undefined;

  for (const category of schema.categories) {
    const field = category.fields.find((f) => f.path === path);
    if (field) return field;
  }

  return undefined;
}

/**
 * Validate a condition
 */
export function validateCondition(
  condition: CriteriaCondition,
  entityType: string
): string | null {
  const field = getFieldByPath(entityType, condition.path);

  if (!field) {
    return `Unknown field path: ${condition.path}`;
  }

  if (!field.operators.includes(condition.operator)) {
    return `Operator "${condition.operator}" not supported for field "${field.label}"`;
  }

  if (condition.value === '' || condition.value === null || condition.value === undefined) {
    return 'Value is required';
  }

  if (condition.operator === 'between') {
    if (!Array.isArray(condition.value) || condition.value.length !== 2) {
      return 'BETWEEN requires exactly 2 values';
    }
  }

  return null;
}
